import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, FloppyDisk, X, CopySimple, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const DEFAULT_PLACEMENT_RULES = { allowed_rotations: [0,1,2,3,4,5], base_only: false, can_support_load: true, min_support_ratio: 1, max_stack_layers: 0 };
const EMPTY = { placement_rules: DEFAULT_PLACEMENT_RULES, sku: "", name: "", gtip: "", description: "", brand: "", barcode: "", unit: "pcs", length: 0, width: 0, height: 0, dimension_unit: "cm", package_kg: 0, product_kg: 0, gross_kg: 0, net_kg: 0, weight_unit: "kg", price: 0, currency: "USD", attributes: {}, active: true, category_id: "" };

const CURRENCIES = ["USD", "GBP", "EUR", "TRY"];
const CURRENCY_SYMBOL = { USD: "$", GBP: "£", EUR: "€", TRY: "₺" };

export default function Products() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkCategory, setBulkCategory] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  const load = async () => {
    const [productsRes, categoriesRes] = await Promise.all([
      api.get("/products"),
      api.get("/product-categories"),
    ]);
    setProducts(productsRes.data);
    setCategories(categoriesRes.data);
  };
  useEffect(() => { load(); }, []);

  const filteredProducts = products.filter((p) => {
    if (filterCategory === "all") return true;
    if (filterCategory === "uncategorized") return !p.category_id;
    return p.category_id === filterCategory;
  });

  const selectedSet = new Set(selected);
  const allSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((p) => selected.includes(p.id));

  const toggleAll = () => {
    const visibleIds = filteredProducts.map((p) => p.id);

    if (allSelected) {
      setSelected((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const addCategory = async () => {
    const name = window.prompt(t("final.products.new_category"));
    if (!name || !name.trim()) return;
    try {
      await api.post("/product-categories", { name: name.trim() });
      toast.success("Kategori eklendi");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const renameCategory = async () => {
    if (!bulkCategory) return toast.error(t("final.products.select_category"));
    const cat = categories.find((c) => c.id === bulkCategory);
    if (!cat) return;
    const name = window.prompt(t("final.products.category_name"), cat.name);
    if (!name || !name.trim() || name.trim() === cat.name) return;
    try {
      await api.put(`/product-categories/${cat.id}`, { name: name.trim() });
      toast.success(t("final.products.updated"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteCategory = async () => {
    if (!bulkCategory) return toast.error(t("final.products.select_category"));
    const cat = categories.find((c) => c.id === bulkCategory);
    if (!cat) return;
    if (!window.confirm(`"${cat.name}" kategorisi silinsin mi? Ürünler silinmez, kategorisiz kalır.`)) return;
    try {
      await api.delete(`/product-categories/${cat.id}`);
      toast.success("Kategori silindi");
      setBulkCategory("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const assignBulkCategory = async () => {
    if (!selected.length) return toast.error(t("final.products.select_product"));
    try {
      await api.post("/products/bulk-category", {
        ids: selected,
        category_id: bulkCategory || null,
      });
      toast.success(`${selected.length} ürünün kategorisi güncellendi`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const categoryName = (id) =>
    categories.find((c) => c.id === id)?.name || "Kategorisiz";

  const prepareForEdit = (p) => {
    const attrs = { ...(p.attributes || {}) };
    if (p.inch && !attrs.inch) attrs.inch = String(p.inch);
    return { ...EMPTY, ...p, gross_kg: p.gross_kg ?? p.net_kg ?? 0, attributes: attrs, placement_rules: { ...DEFAULT_PLACEMENT_RULES, ...(p.placement_rules || {}) } };
  };

  const setAttribute = (oldKey, nextKey, value) => {
    const attrs = { ...(editing.attributes || {}) };
    if (oldKey !== nextKey) delete attrs[oldKey];
    if (nextKey.trim()) attrs[nextKey] = value;
    setEditing({ ...editing, attributes: attrs });
  };
  const addAttribute = () => {
    const attrs = { ...(editing.attributes || {}) };
    let i = 1;
    while (attrs[`attribute_${i}`] !== undefined) i += 1;
    attrs[`attribute_${i}`] = "";
    setEditing({ ...editing, attributes: attrs });
  };
  const removeAttribute = (key) => {
    const attrs = { ...(editing.attributes || {}) };
    delete attrs[key];
    setEditing({ ...editing, attributes: attrs });
  };

  const save = async () => {
    try {
      const attrs = editing.attributes || {};
      const payload = { ...editing, inch: attrs.inch || editing.inch || "", gross_kg: Number(editing.gross_kg || 0), net_kg: Number(editing.gross_kg || editing.net_kg || 0) };
      if (editing.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Kaydedildi");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/products/${id}`); toast.success("Silindi"); load();
  };

  const duplicate = (p) => {
    // Open form pre-filled with existing product data (no id), user must set a unique SKU
    const clone = { ...p };
    delete clone.id;
    clone.sku = `${p.sku}-COPY`;
    setEditing(prepareForEdit(clone));
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("pallet_token");
      const res = await fetch(`${API}/products/import/template.xlsx`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Şablon indirilemedi");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "products_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const exportSelected = async () => {
    if (!selected.length) return toast.error("Dışa aktarmak için en az bir ürün seçin");
    try {
      const token = localStorage.getItem("pallet_token");
      const params = new URLSearchParams({ ids: selected.join(",") });
      const res = await fetch(`${API}/products/export.xlsx?${params.toString()}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let detail = "Dışa aktarma başarısız";
        try { const data = await res.json(); detail = data?.detail || detail; } catch {}
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url; a.download = `selected_products_${stamp}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${selected.length} seçili ürün dışa aktarıldı`);
    } catch (e) { toast.error(e.message); }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      toast.success(`İçe aktarıldı: ${d.created} yeni, ${d.updated} güncelleme${d.errors.length ? `, ${d.errors.length} hata` : ""}`);
      if (d.errors && d.errors.length) {
        console.warn("Import errors:", d.errors);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader title={t("products.title")} subtitle={t("products.subtitle", { products: products.length, categories: categories.length })}>
        <button onClick={downloadTemplate} data-testid="download-template"
          className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 text-sm font-semibold rounded-sm">
          <DownloadSimple size={14} weight="bold" /> {t("products.template")}
        </button>
        <button onClick={exportSelected} disabled={!selected.length} data-testid="export-selected"
          className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 text-sm font-semibold rounded-sm disabled:opacity-40 disabled:cursor-not-allowed">
          <DownloadSimple size={14} weight="bold" /> Seçilenleri Dışa Aktar
        </button>
        <label data-testid="import-excel-label"
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 text-sm font-semibold rounded-sm cursor-pointer">
          <UploadSimple size={14} weight="bold" /> {t("products.import_excel")}
          <input type="file" accept=".xlsx" onChange={importExcel} className="hidden" data-testid="import-excel-input" />
        </label>
        <button onClick={() => setEditing({ ...EMPTY, attributes: {}, placement_rules: { ...DEFAULT_PLACEMENT_RULES } })} data-testid="add-product"
          className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 text-sm font-semibold rounded-sm">
          <Plus size={16} weight="bold" /> {t("products.new_product")}
        </button>
      </PageHeader>
      <div className="p-4 sm:p-8">
        <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-2">

          <div className="text-xs font-semibold text-slate-600 mr-1">
            Filtre
          </div>

          <select
            className="input !w-auto min-w-[180px]"
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setSelected([]);
            }}
            data-testid="category-filter"
          >
            <option value="all">{t("products.all_categories")}</option>
            <option value="uncategorized">{t("products.uncategorized")}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <div className="h-7 w-px bg-slate-200 mx-1" />

          <div className="text-xs font-semibold text-slate-600 mr-1">
            {selected.length ? t("products.selected_count", { n: selected.length }) : t("products.category_assignment")}
          </div>

          <select
            className="input !w-auto min-w-[180px]"
            value={bulkCategory}
            onChange={(e) => setBulkCategory(e.target.value)}
            data-testid="bulk-category-select"
          >
            <option value="">{t("products.uncategorized")}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <button
            onClick={assignBulkCategory}
            disabled={!selected.length}
            className="px-3 py-2 text-xs font-semibold bg-[#2563EB] text-white disabled:opacity-40 rounded-sm"
          >
            {t("products.assign_category")}
          </button>

          <div className="h-7 w-px bg-slate-200 mx-1" />

          <button
            onClick={addCategory}
            className="px-3 py-2 text-xs font-semibold border border-slate-300 bg-white hover:bg-slate-50 rounded-sm"
          >
            {t("products.add_category")}
          </button>

          <button
            onClick={renameCategory}
            disabled={!bulkCategory}
            className="px-3 py-2 text-xs font-semibold border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 rounded-sm"
          >
            {t("products.edit_category")}
          </button>

          <button
            onClick={deleteCategory}
            disabled={!bulkCategory}
            className="px-3 py-2 text-xs font-semibold border border-red-200 text-red-600 bg-white hover:bg-red-50 disabled:opacity-40 rounded-sm"
          >
            {t("products.delete_category")}
          </button>
        </div>

        <div className="bg-white border border-slate-200 overflow-x-auto rounded-sm">
          <table className="data-table w-full min-w-[1050px]">
            <thead><tr>
              <th className="w-10 text-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label={t("final.products.select_all")}
                />
              </th>
              <th>Ürün</th><th>SKU</th><th>{t("products.category")}</th><th>GTIP / HS</th><th>{t("products.dimensions")}</th>
              <th className="text-right">Brüt Ağırlık</th><th className="text-right">{t("products.price")}</th><th>Durum</th><th></th>
            </tr></thead>
            <tbody>
              {filteredProducts.map((p) => (
                <tr key={p.id} data-testid={`product-${p.sku}`}>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedSet.has(p.id)}
                      onChange={() => toggleOne(p.id)}
                      aria-label={`${p.sku} seç`}
                    />
                  </td>
                  <td><div className="font-semibold text-slate-900">{p.name || p.sku}</div>{(p.attributes?.inch || p.inch) && <div className="text-[10px] text-slate-500 mt-0.5">{p.attributes?.inch || p.inch}"</div>}</td>
                  <td className="font-mono text-xs">{p.sku}</td>
                  <td>
                    <span className={`text-[10px] px-2 py-1 border rounded-sm ${
                      p.category_id
                        ? "bg-blue-50 border-blue-200 text-blue-700"
                        : "bg-slate-50 border-slate-200 text-slate-500"
                    }`}>
                      {categoryName(p.category_id)}
                    </span>
                  </td>
                  <td className="font-mono text-xs text-slate-500">{p.gtip}</td>
                  <td className="font-mono text-xs">{p.length}×{p.width}×{p.height} {p.dimension_unit || "cm"}</td>
                  <td className="text-right data-cell">{Number(p.gross_kg ?? p.net_kg ?? 0).toFixed(2)} {p.weight_unit || "kg"}</td>
                  <td className="text-right data-cell font-mono">{CURRENCY_SYMBOL[p.currency] || "$"}{Number(p.price || 0).toFixed(2)}</td>
                  <td><span className={`text-[10px] px-2 py-1 border rounded-sm ${p.active !== false ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-500"}`}>{p.active !== false ? "Aktif" : "Pasif"}</span></td>
                  <td className="text-right">
                    <button onClick={() => setEditing(prepareForEdit(p))} className="text-slate-500 hover:text-[#2563EB] mr-3" title={t("final.products.edit")} data-testid={`edit-${p.sku}`}><PencilSimple size={16} /></button>
                    <button onClick={() => duplicate(p)} className="text-slate-500 hover:text-[#2563EB] mr-3" title="Kopyala" data-testid={`dup-${p.sku}`}><CopySimple size={16} /></button>
                    <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-600" title="Sil" data-testid={`del-${p.sku}`}><Trash size={16} /></button>
                  </td>
                </tr>
              ))}

              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-slate-400">
                    {t("products.no_products")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="product-modal">
          <div className="bg-white w-full max-w-4xl border border-slate-300 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-center px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
              <div><h3 className="font-display font-bold">{editing.id ? t("products.edit_product") : t("products.new_product")}</h3><p className="text-xs text-slate-500 mt-0.5">Universal ürün kartı · çekirdek bilgiler + fiziksel veriler + dinamik özellikler</p></div>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-5">
              <section><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Genel Bilgiler</div><div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[['name','Ürün Adı'],['sku','SKU / Kod'],['brand','Marka'],['barcode','Barkod'],['gtip','GTIP / HS Code'],['unit','Birim']].map(([k,l]) => <label key={k} className="text-xs uppercase font-semibold tracking-wider text-slate-500">{l}<input data-testid={`input-${k}`} className="input block mt-1" value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} /></label>)}
                <label className="text-xs uppercase font-semibold tracking-wider text-slate-500">Kategori<select data-testid="input-category" className="input block mt-1" value={editing.category_id || ""} onChange={(e) => setEditing({ ...editing, category_id: e.target.value || null })}><option value="">{t("products.uncategorized")}</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
                <label className="text-xs uppercase font-semibold tracking-wider text-slate-500 md:col-span-2">Açıklama<input data-testid="input-description" className="input block mt-1" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></label>
                <label className="text-xs uppercase font-semibold tracking-wider text-slate-500 flex items-end gap-2 pb-2"><input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Aktif ürün</label>
              </div></section>
              <section className="border-t border-slate-200 pt-4"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Fiziksel Bilgiler</div><div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[['length','Uzunluk'],['width','Genişlik'],['height','Yükseklik'],['product_kg','Ürün Ağırlığı'],['package_kg','Ambalaj Ağırlığı'],['gross_kg','Brüt Ağırlık']].map(([k,l]) => <label key={k} className="text-xs uppercase font-semibold tracking-wider text-slate-500">{l}<input type="number" step="0.01" min="0" data-testid={`input-${k}`} className="input block mt-1" value={editing[k] ?? 0} onChange={(e) => setEditing({ ...editing, [k]: Number(e.target.value) })} /></label>)}
                <label className="text-xs uppercase font-semibold tracking-wider text-slate-500">Ölçü Birimi<select className="input block mt-1" value={editing.dimension_unit || 'cm'} onChange={(e) => setEditing({ ...editing, dimension_unit: e.target.value })}><option value="mm">mm</option><option value="cm">cm</option><option value="m">m</option></select></label>
                <label className="text-xs uppercase font-semibold tracking-wider text-slate-500">Ağırlık Birimi<select className="input block mt-1" value={editing.weight_unit || 'kg'} onChange={(e) => setEditing({ ...editing, weight_unit: e.target.value })}><option value="g">g</option><option value="kg">kg</option></select></label>
              </div><div className="mt-2 text-[11px] text-slate-500">Rakamlar seçtiğiniz birime göre yorumlanır: 600 mm = 60 cm, 1500 g = 1,5 kg. Birimi değiştirmek yazdığınız rakamları değiştirmez.</div></section>
              <section className="border-t border-slate-200 pt-4"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Fiyatlandırma</div><div className="grid grid-cols-2 md:grid-cols-4 gap-3"><label className="text-xs uppercase font-semibold tracking-wider text-slate-500">Birim Fiyat<input type="number" step="0.01" min={0} data-testid="input-price" className="input block mt-1" value={editing.price ?? 0} onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })} /></label><label className="text-xs uppercase font-semibold tracking-wider text-slate-500">Para Birimi<select data-testid="input-currency" className="input block mt-1" value={editing.currency || "USD"} onChange={(e) => setEditing({ ...editing, currency: e.target.value })}>{CURRENCIES.map((c) => <option key={c} value={c}>{CURRENCY_SYMBOL[c]} {c}</option>)}</select></label></div></section>
              <section className="border-t border-slate-200 pt-4" data-testid="placement-rules-section">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Yerleşim Kuralları</div>
                <div className="text-xs text-slate-400 mb-3">Optimizasyon ve manuel yerleşimde bu ürün için geçerli fiziksel kısıtlar.</div>
                <div className="mb-3"><div className="text-xs font-semibold text-slate-600 mb-2">İzin verilen rotasyonlar</div><div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[['0','D×G×Y'],['1','D×Y×G'],['2','G×D×Y'],['3','G×Y×D'],['4','Y×D×G'],['5','Y×G×D']].map(([id,label]) => { const n=Number(id); const allowed=editing.placement_rules?.allowed_rotations || [0,1,2,3,4,5]; return <label key={id} className="flex items-center gap-2 text-xs border border-slate-200 p-2"><input type="checkbox" data-testid={`rule-rotation-${id}`} checked={allowed.includes(n)} onChange={(e) => { const next=e.target.checked ? [...new Set([...allowed,n])].sort() : allowed.filter((x)=>x!==n); if (!next.length) return; setEditing({...editing, placement_rules:{...DEFAULT_PLACEMENT_RULES,...editing.placement_rules,allowed_rotations:next}}); }} /> {label}</label>; })}
                </div></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 text-xs border border-slate-200 p-3"><input type="checkbox" data-testid="rule-base-only" checked={!!editing.placement_rules?.base_only} onChange={(e)=>setEditing({...editing,placement_rules:{...DEFAULT_PLACEMENT_RULES,...editing.placement_rules,base_only:e.target.checked}})} /> Sadece palet tabanına yerleşebilir</label>
                  <label className="flex items-center gap-2 text-xs border border-slate-200 p-3"><input type="checkbox" data-testid="rule-can-support" checked={editing.placement_rules?.can_support_load !== false} onChange={(e)=>setEditing({...editing,placement_rules:{...DEFAULT_PLACEMENT_RULES,...editing.placement_rules,can_support_load:e.target.checked}})} /> Üzerine başka kutu konabilir</label>
                  <label className="text-xs uppercase font-semibold tracking-wider text-slate-500">Minimum Destek %<input type="number" min="0" max="100" step="5" data-testid="rule-min-support" className="input block mt-1" value={Math.round((editing.placement_rules?.min_support_ratio ?? 1)*100)} onChange={(e)=>setEditing({...editing,placement_rules:{...DEFAULT_PLACEMENT_RULES,...editing.placement_rules,min_support_ratio:Math.max(0,Math.min(1,Number(e.target.value)/100))}})} /></label>
                  <label className="text-xs uppercase font-semibold tracking-wider text-slate-500">Maksimum Kat <span className="normal-case font-normal">(0 = sınırsız)</span><input type="number" min="0" step="1" data-testid="rule-max-layers" className="input block mt-1" value={editing.placement_rules?.max_stack_layers ?? 0} onChange={(e)=>setEditing({...editing,placement_rules:{...DEFAULT_PLACEMENT_RULES,...editing.placement_rules,max_stack_layers:Math.max(0,Number(e.target.value)||0)}})} /></label>
                </div>
              </section>
              <section className="border-t border-slate-200 pt-4"><div className="flex items-center justify-between mb-2"><div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Dinamik Özellikler</div><div className="text-xs text-slate-400 mt-0.5">İnç, renk, malzeme, model, voltaj gibi ürüne özel alanlar.</div></div><button type="button" onClick={addAttribute} className="px-3 py-1.5 text-xs font-semibold border border-slate-300 hover:bg-slate-50 rounded-sm"><Plus size={12} className="inline mr-1" /> Özellik Ekle</button></div><div className="space-y-2">{Object.entries(editing.attributes || {}).map(([key,value]) => <div key={key} className="grid grid-cols-[1fr_1fr_auto] gap-2"><input className="input" defaultValue={key} placeholder="Alan adı" onBlur={(e) => setAttribute(key, e.target.value, value)} /><input className="input" value={value} placeholder="Değer" onChange={(e) => setAttribute(key, key, e.target.value)} /><button type="button" onClick={() => removeAttribute(key)} className="px-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-sm"><Trash size={14} /></button></div>)}{Object.keys(editing.attributes || {}).length === 0 && <div className="text-xs text-slate-400 border border-dashed border-slate-300 p-3">Henüz özel özellik yok. Örn: inch=43, color=black.</div>}</div></section>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white"><button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm">{t("common.cancel")}</button><button onClick={save} data-testid="save-product" className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 text-sm font-semibold rounded-sm"><FloppyDisk size={14} weight="bold" /> {t("final.save")}</button></div>
          </div>
        </div>
      )}
      <style>{`.input{width:100%;border:1px solid #cbd5e1;padding:0.4rem 0.55rem;font-size:0.85rem;font-family:'JetBrains Mono',monospace;outline:none}.input:focus{border-color:#2563EB;box-shadow:0 0 0 3px rgba(37,99,235,0.15)}`}</style>
    </div>
  );
}
